import React, { useEffect } from 'react';
import { APITypes } from '../../../../../../primary-record-types';
import { getSpacing } from '../../../../../../utils/spacing';
import { Platform, UIManager, View, findNodeHandle } from 'react-native';
import Stack from '../../../../../v2BaseComponents/Stack';
import { ChatMessage, ChatMessageMessage, ModelProcessingPlaceholder } from './ChatEntries';
import * as luxon from 'luxon';
import Text from '../../../../../v2BaseComponents/Text';
import Divider from '../../../../../v2BaseComponents/Divider';
import { PRScrollView } from '../../../../../shared/Scroll';
import { mainContentPadding } from '../../../../../v2BaseComponents/MainContentContainer';

type MessageChunk = {
    messages: ChatMessageMessage[];
    startDateTimeString: string;
};

const formatMessageDateTime = (date: Date): string => {
    const inputDate = luxon.DateTime.fromJSDate(date);
    const formattedDateBase = inputDate.hasSame(luxon.DateTime.now(), 'day')
        ? `Today ${inputDate.toFormat('hh:mm')}`
        : inputDate.toFormat('ccc, MMM dd hh:mm');
    return `${formattedDateBase.toLocaleUpperCase()}${inputDate.toFormat('a').toLocaleLowerCase()}`;
};

const chunkMessagesByHour = (messages: ChatMessageMessage[]): MessageChunk[] => {
    const messageChunks: (Pick<MessageChunk, 'messages'> & {
        startDateTime: Date;
    })[] = [];

    for (const message of messages) {
        if (messageChunks.length === 0) {
            messageChunks.push({
                messages: [message],
                startDateTime: new Date(message.messageDateTime),
            });
            continue;
        }
        const lastChunk = messageChunks[messageChunks.length - 1];
        const lastChunkStartDateTime = lastChunk.startDateTime;
        const messageDateTime = new Date(message.messageDateTime);
        const hoursDiff = (messageDateTime.getTime() - lastChunkStartDateTime.getTime()) / 1000 / 60 / 60;
        if (hoursDiff < 1) lastChunk.messages.push(message);
        else messageChunks.push({ messages: [message], startDateTime: messageDateTime });
    }

    return messageChunks.map((chunk) => ({
        messages: chunk.messages,
        startDateTimeString: formatMessageDateTime(chunk.startDateTime),
    }));
};

const SharedListComponent: React.FC<{
    messages: ChatMessageMessage[];
    showModelProcessing: boolean;
    scrollViewRef?: React.MutableRefObject<any>;
    messageRefs?: (View | null)[];
}> = ({ messages, showModelProcessing, scrollViewRef, messageRefs }) => {
    const chunkedMessages = chunkMessagesByHour(messages);

    // Don't show extra placeholder if there's already a processing message in the list
    const hasProcessingMessage = messages.some((m) => m.role === 'model' && 'status' in m && m.status === 'processing');
    const shouldShowExtraPlaceholder = showModelProcessing && !hasProcessingMessage;

    return (
        <View
            style={{
                display: 'flex',
                flex: 1,
                width: '100%',
            }}
        >
            <PRScrollView
                ref={scrollViewRef}
                style={{
                    width: '100%',
                }}
                contentContainerStyle={{
                    flexGrow: 1,
                }}
            >
                <Stack
                    flex={1}
                    vertical
                    style={{
                        justifyContent: 'flex-start',
                        paddingHorizontal: getSpacing(2),
                        paddingBottom: getSpacing(2),
                        paddingTop: getSpacing(1),
                    }}
                    space={getSpacing(1)}
                >
                    {chunkedMessages.map((chunk, index) => {
                        const previousChunksMessageCount = [...chunkedMessages].splice(0, index).reduce((acc, curr) => {
                            return acc + curr.messages.length;
                        }, 0);
                        return (
                            <Stack
                                vertical
                                style={{
                                    width: '100%',
                                }}
                                key={chunk.startDateTimeString + index}
                            >
                                <Stack
                                    horizontal
                                    space={getSpacing(4)}
                                    style={{
                                        width: '100%',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        paddingHorizontal: getSpacing(4),
                                        paddingVertical: getSpacing(8),
                                    }}
                                >
                                    <Divider horizontal style={{ flex: 1 }} />
                                    <Text variant="caption-1">{chunk.startDateTimeString}</Text>
                                    <Divider horizontal style={{ flex: 1 }} />
                                </Stack>
                                {chunk.messages.map((message, index) => {
                                    return (
                                        <ChatMessage
                                            key={message.messageDateTime + index}
                                            message={message}
                                            ref={(ref) => {
                                                if (messageRefs) messageRefs[previousChunksMessageCount + index] = ref;
                                            }}
                                        />
                                    );
                                })}
                            </Stack>
                        );
                    })}
                    {shouldShowExtraPlaceholder ? <ModelProcessingPlaceholder /> : null}
                </Stack>
            </PRScrollView>
        </View>
    );
};

export type ConversationListOwnProps = {
    chat: APITypes.chatService.MedicalProfileChat;
    isWaitingOnChatMessage: boolean;
};

export type ConversationListProps = ConversationListOwnProps;

const ConversationList: React.FC<ConversationListProps> = (props) => {
    const scrollViewRef = React.useRef<any>(null);
    const messageRefs = React.useRef<(View | null)[]>([]);
    const messageCount = props.chat.messages.length;
    const isInitialUseMountEffect = React.useRef(true);
    useEffect(() => {
        const useLastMessage = !isInitialUseMountEffect.current;
        isInitialUseMountEffect.current = false;
        // Delay the scroll slightly, if we don't it doesn't go all the way to the bottom
        const timeout = setTimeout(() => {
            // if initial mount, scroll to last user message, else scroll to last model message
            const lastMessageRef = messageRefs.current[messageCount - (useLastMessage ? 1 : 2)];
            if (lastMessageRef && scrollViewRef.current) {
                if (Platform.OS === 'web') {
                    // findNodeHandle is unsupported on web in RN 0.79+. Use the ref's measureLayout directly.
                    lastMessageRef.measureLayout(
                        scrollViewRef.current.getInnerViewNode(),
                        (x, y) => {
                            scrollViewRef.current.scrollTo({ y: y - mainContentPadding, animated: true });
                        },
                        () => {},
                    );
                } else {
                    const targetViewNodeHandle = findNodeHandle(lastMessageRef);
                    UIManager.measureLayout(
                        targetViewNodeHandle!,
                        scrollViewRef.current.getInnerViewNode(),
                        () => {},
                        (x, y) => {
                            scrollViewRef.current.scrollTo({ y: y - mainContentPadding, animated: true });
                        },
                    );
                }
            }
        }, 700);
        return () => clearTimeout(timeout);
    }, [messageCount]);
    return (
        <SharedListComponent
            scrollViewRef={scrollViewRef}
            messages={props.chat.messages}
            showModelProcessing={props.isWaitingOnChatMessage}
            messageRefs={messageRefs.current}
        />
    );
};

export default React.memo(ConversationList);

export const InitialMessageSentPlaceholderConversationList: React.FC<{
    initialMessage: string;
}> = ({ initialMessage }) => {
    return (
        <SharedListComponent
            messages={[
                {
                    type: 'placeholder',
                    role: 'user',
                    text: initialMessage,
                    messageDateTime: Date.now(),
                },
            ]}
            showModelProcessing={true}
        />
    );
};
